import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-oris-green/20 bg-oris-cream px-4 py-3.5 text-sm text-oris-ink placeholder:text-oris-moss/50 transition-[border-color,box-shadow] duration-300 focus:border-oris-gold";

export const Contact = () => {
  const { t } = useLang();
  const f = t.contact.form;
  const [form, setForm] = useState({ name: "", company: "", email: "", subject: f.subjectOptions[0], message: "" });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(f.success);
      setForm({ name: "", company: "", email: "", subject: f.subjectOptions[0], message: "" });
    } catch (err) {
      toast.error(f.error);
    } finally {
      setSending(false);
    }
  };

  const info = t.contact.info;
  const rows = [
    ...info.offices.map((o, i) => ({ icon: MapPin, label: o.label, value: o.value, testId: `contact-address-${i}` })),
    ...info.phones.map((p, i) => ({ icon: Phone, label: p.label, value: p.value, testId: `contact-phone-${i}` })),
    { icon: Mail, label: info.emailLabel, value: info.email, testId: "contact-email" },
  ];

  return (
    <section id="contact" data-testid="contact-section" className="bg-oris-cream py-24 lg:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead overline={t.contact.overline} title={t.contact.title} testId="contact-title" />

        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="max-w-md text-base leading-relaxed text-oris-moss lg:text-lg">{t.contact.intro}</p>
            </Reveal>

            <div className="mt-10 divide-y divide-oris-green/10 border-y border-oris-green/10">
              {rows.map((r, i) => (
                <Reveal key={r.label} delay={i * 0.07}>
                  <div data-testid={r.testId} className="flex items-start gap-5 py-6">
                    <r.icon className="mt-1 h-5 w-5 shrink-0 text-oris-gold" strokeWidth={1.8} />
                    <div>
                      <div className="text-[0.68rem] font-bold uppercase tracking-[0.22em] text-oris-moss">{r.label}</div>
                      <div className="mt-1 font-serif text-xl text-oris-ink">{r.value}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.2}>
              <div data-testid="contact-map" className="spotlight-frame mt-10 overflow-hidden border border-oris-green/15">
                <iframe
                  title="ORIS INTERNATIONAL — Abidjan"
                  src="https://www.google.com/maps?q=Marcory+R%C3%A9sidentiel,+Abidjan,+C%C3%B4te+d%27Ivoire&output=embed"
                  className="h-64 w-full grayscale-[35%]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                data-testid="contact-form"
                onSubmit={submit}
                className="border border-oris-green/12 bg-white p-7 shadow-[0_18px_60px_rgb(15,41,24,0.07)] sm:p-10"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                      {f.name} *
                    </label>
                    <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                      {f.company}
                    </label>
                    <input id="contact-company" data-testid="contact-company-input" value={form.company} onChange={set("company")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                      {f.email} *
                    </label>
                    <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                      {f.subject}
                    </label>
                    <select id="contact-subject" data-testid="contact-subject-select" value={form.subject} onChange={set("subject")} className={inputCls}>
                      {f.subjectOptions.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="contact-message" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                      {f.message} *
                    </label>
                    <textarea
                      id="contact-message"
                      data-testid="contact-message-input"
                      required
                      rows={6}
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputCls} resize-y`}
                    />
                  </div>
                </div>

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={sending}
                  className="group mt-8 inline-flex items-center gap-3 bg-oris-green px-8 py-4 text-[0.8rem] font-bold uppercase tracking-[0.16em] text-oris-cream transition-colors duration-300 hover:bg-oris-deep disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      {f.sending}
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      {f.submit}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </>
                  )}
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
